import { useCallback, useEffect, useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import {
  buildNameToFieldMeta,
  formatKeyForDisplay,
  getComparableKey,
} from "./itemFieldComparableKey"
import type { ExportFieldOption } from "./ComponentTableExportDialog"
import type { DuplicateFilterEntry } from "./useComponentTableFilters"

export type ComponentTableDuplicatesDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  rows: Record<string, unknown>[]
  exportableFieldsFromMeta: ExportFieldOption[]
  meta: unknown
  currentMeta: unknown
  onApplyDuplicateFilters: (entries: DuplicateFilterEntry[]) => void
}

type CombineMode = "and" | "or"

const MAX_GROUPS_SHOWN = 150

const duplicateResultCardButtonClass = cn(
  "group w-full rounded-lg border border-border bg-card text-card-foreground shadow-sm",
  "p-3.5 flex justify-between gap-3 items-start text-left",
  "transition-all duration-200",
  "hover:shadow-md hover:border-primary/30 hover:bg-accent/50",
  "active:scale-[0.995]",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
)

export function ComponentTableDuplicatesDialog({
  open,
  onOpenChange,
  rows,
  exportableFieldsFromMeta,
  meta,
  currentMeta,
  onApplyDuplicateFilters,
}: ComponentTableDuplicatesDialogProps) {
  const [selectedFields, setSelectedFields] = useState<string[]>([])
  const [combineMode, setCombineMode] = useState<CombineMode>("and")
  const [includeEmptyAsMatch, setIncludeEmptyAsMatch] = useState(true)

  useEffect(() => {
    if (open) {
      setSelectedFields([])
      setCombineMode("and")
      setIncludeEmptyAsMatch(true)
    }
  }, [open])

  const nameToFieldMeta = useMemo(() => {
    const merged = { ...buildNameToFieldMeta(meta), ...buildNameToFieldMeta(currentMeta) }
    return merged
  }, [meta, currentMeta])

  const fieldLabel = useCallback(
    (name: string) => exportableFieldsFromMeta.find((f) => f.name === name)?.label ?? name,
    [exportableFieldsFromMeta]
  )

  const allFieldsSelected = useMemo(
    () =>
      exportableFieldsFromMeta.length > 0 &&
      exportableFieldsFromMeta.every((f) => selectedFields.includes(f.name)),
    [exportableFieldsFromMeta, selectedFields]
  )

  const andResults = useMemo(() => {
    if (combineMode !== "and" || selectedFields.length === 0) return null
    const map = new Map<string, { parts: string[]; count: number }>()
    const safeRows = Array.isArray(rows) ? rows : []
    for (const row of safeRows) {
      const r = row as Record<string, unknown>
      const parts = selectedFields.map((f) => getComparableKey(r, f, nameToFieldMeta))
      const composite = parts.join("\x1e")
      const cur = map.get(composite)
      if (cur) cur.count += 1
      else map.set(composite, { parts, count: 1 })
    }
    let groups = [...map.values()].filter((g) => g.count >= 2)
    if (!includeEmptyAsMatch) {
      groups = groups.filter((g) => !g.parts.some((p) => p === "__empty__"))
    }
    groups.sort((a, b) => b.count - a.count)
    const totalRowsInGroups = groups.reduce((s, g) => s + g.count, 0)
    return { groups, totalRowsInGroups }
  }, [combineMode, selectedFields, rows, nameToFieldMeta, includeEmptyAsMatch])

  const orResults = useMemo(() => {
    if (combineMode !== "or" || selectedFields.length === 0) return null
    const safeRows = Array.isArray(rows) ? rows : []
    const countMaps = selectedFields.map(() => new Map<string, number>())
    for (const row of safeRows) {
      const r = row as Record<string, unknown>
      selectedFields.forEach((fieldName, i) => {
        const k = getComparableKey(r, fieldName, nameToFieldMeta)
        if (!includeEmptyAsMatch && k === "__empty__") return
        const m = countMaps[i]!
        m.set(k, (m.get(k) ?? 0) + 1)
      })
    }
    const perField = selectedFields.map((fieldName, i) => {
      const keyCount = countMaps[i]!
      const groups = [...keyCount.entries()]
        .filter(([, c]) => c >= 2)
        .map(([key, count]) => ({ key, count }))
      groups.sort((a, b) => b.count - a.count)
      return { fieldName, fieldLabel: fieldLabel(fieldName), groups }
    })

    let rowsInAnyDupGroup = 0
    for (const row of safeRows) {
      const r = row as Record<string, unknown>
      let inDup = false
      for (let fi = 0; fi < selectedFields.length; fi++) {
        const k = getComparableKey(r, selectedFields[fi]!, nameToFieldMeta)
        if ((countMaps[fi]!.get(k) ?? 0) >= 2) {
          inDup = true
          break
        }
      }
      if (inDup) rowsInAnyDupGroup += 1
    }

    return { perField, rowsInAnyDupGroup }
  }, [combineMode, selectedFields, rows, nameToFieldMeta, fieldLabel, includeEmptyAsMatch])

  const orPerFieldSplit = useMemo(() => {
    if (!orResults) return null
    const withDups = orResults.perField.filter((b) => b.groups.length > 0)
    const withoutDups = orResults.perField.filter((b) => b.groups.length === 0)
    return { withDups, withoutDups }
  }, [orResults])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,720px)] min-h-0 w-full flex-col gap-4 overflow-hidden p-6 sm:max-w-lg">
        <DialogHeader className="shrink-0">
          <DialogTitle>Find duplicates</DialogTitle>
          <DialogDescription>
            Choose one or more fields. Rows currently visible in the table (after column filters) are analyzed.{" "}
            <span className="font-medium text-foreground">All</span> means the same combination of values;{" "}
            <span className="font-medium text-foreground">Any</span> shows duplicate value groups per field. Click a
            result to replace column filters and show only matching rows (page resets to 1).
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
          <div className="flex shrink-0 flex-col gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Match</span>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="dupCombine"
                  checked={combineMode === "and"}
                  onChange={() => setCombineMode("and")}
                  className="rounded-full border-gray-300"
                />
                <span className="text-sm">All selected fields (AND)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="dupCombine"
                  checked={combineMode === "or"}
                  onChange={() => setCombineMode("or")}
                  className="rounded-full border-gray-300"
                />
                <span className="text-sm">Any selected field (OR)</span>
              </label>
            </div>
            <label className="flex cursor-pointer items-start gap-2 pt-1">
              <Checkbox
                checked={includeEmptyAsMatch}
                onCheckedChange={(checked) => setIncludeEmptyAsMatch(checked === true)}
                className="mt-0.5"
              />
              <span className="text-sm leading-snug text-gray-700 dark:text-gray-300">
                Include empty as a match{" "}
                <span className="text-muted-foreground">
                  (treat blank values as one duplicate group per field)
                </span>
              </span>
            </label>
          </div>

          <div className="flex shrink-0 flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Fields</span>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  disabled={exportableFieldsFromMeta.length === 0 || allFieldsSelected}
                  onClick={() =>
                    setSelectedFields(exportableFieldsFromMeta.map((f) => f.name))
                  }
                >
                  Select all
                </Button>
                <span className="text-xs text-muted-foreground" aria-hidden>
                  |
                </span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  disabled={selectedFields.length === 0}
                  onClick={() => setSelectedFields([])}
                >
                  Select none
                </Button>
              </div>
            </div>
            <div className="max-h-40 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 p-3 space-y-2 shrink-0">
              {exportableFieldsFromMeta.map((f) => (
                <label key={f.name} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedFields.includes(f.name)}
                    onCheckedChange={(checked) => {
                      setSelectedFields((prev) =>
                        checked ? [...prev, f.name] : prev.filter((x) => x !== f.name)
                      )
                    }}
                  />
                  <span className="text-sm">{f.label}</span>
                </label>
              ))}
            </div>
          </div>

          {selectedFields.length > 0 && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-gray-200 bg-gray-50/80 dark:border-gray-700 dark:bg-gray-900/40">
              <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400 shrink-0">
                {Array.isArray(rows) ? rows.length : 0} rows analyzed
                {combineMode === "and" && andResults ? (
                  <>
                    {" · "}
                    {andResults.groups.length} duplicate {andResults.groups.length === 1 ? "group" : "groups"},{" "}
                    {andResults.totalRowsInGroups} items in those groups
                  </>
                ) : null}
                {combineMode === "or" && orResults ? (
                  <>
                    {" · "}
                    {orResults.rowsInAnyDupGroup} rows appear in at least one duplicate group
                  </>
                ) : null}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-3 text-sm space-y-4">
                {combineMode === "and" && andResults ? (
                  andResults.groups.length === 0 ? (
                    <p className="text-muted-foreground">No duplicates for this combination.</p>
                  ) : (
                    <ul className="space-y-3 list-none m-0 p-0">
                      {andResults.groups.slice(0, MAX_GROUPS_SHOWN).map((g, idx) => (
                        <li key={`${g.parts.join("\x1e")}-${idx}`} className="m-0 p-0">
                          <button
                            type="button"
                            title="Apply this combination as column filters"
                            className={duplicateResultCardButtonClass}
                            onClick={() => {
                              onApplyDuplicateFilters(
                                selectedFields.map((fn, i) => ({
                                  fieldName: fn,
                                  comparableKey: g.parts[i] ?? "__empty__",
                                }))
                              )
                              onOpenChange(false)
                            }}
                          >
                            <span className="min-w-0 flex-1 space-y-1.5">
                              {selectedFields.map((fn, i) => (
                                <span key={fn} className="block leading-snug">
                                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                    {fieldLabel(fn)}
                                  </span>
                                  <span className="block text-sm font-medium text-foreground group-hover:text-foreground">
                                    {formatKeyForDisplay(fn, g.parts[i] ?? "__empty__")}
                                  </span>
                                </span>
                              ))}
                            </span>
                            <span className="shrink-0 rounded-md bg-primary/10 px-2 py-1 text-sm font-semibold tabular-nums text-primary group-hover:bg-primary/15">
                              {g.count}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )
                ) : null}

                {combineMode === "or" && orResults && orPerFieldSplit ? (
                  orResults.perField.every((b) => b.groups.length === 0) ? (
                    <p className="text-muted-foreground">No duplicates for the selected fields.</p>
                  ) : (
                    <>
                      <div className="space-y-4">
                        {orPerFieldSplit.withDups.map((block) => (
                          <div key={block.fieldName} className="space-y-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-0.5">
                              {block.fieldLabel}
                            </div>
                            <ul className="space-y-3 list-none m-0 p-0">
                              {block.groups.slice(0, MAX_GROUPS_SHOWN).map((gr) => (
                                <li key={`${block.fieldName}-${gr.key}`} className="m-0 p-0">
                                  <button
                                    type="button"
                                    title={`Apply ${block.fieldLabel} filter`}
                                    className={duplicateResultCardButtonClass}
                                    onClick={() => {
                                      onApplyDuplicateFilters([
                                        { fieldName: block.fieldName, comparableKey: gr.key },
                                      ])
                                      onOpenChange(false)
                                    }}
                                  >
                                    <span className="min-w-0 flex-1 text-sm font-medium leading-snug text-foreground group-hover:text-foreground">
                                      {formatKeyForDisplay(block.fieldName, gr.key)}
                                    </span>
                                    <span className="shrink-0 rounded-md bg-primary/10 px-2 py-1 text-sm font-semibold tabular-nums text-primary group-hover:bg-primary/15">
                                      {gr.count}
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      {orPerFieldSplit.withoutDups.length > 0 ? (
                        <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                          <p className="text-xs font-medium text-muted-foreground px-0.5">
                            No duplicates for these fields
                          </p>
                          {orPerFieldSplit.withoutDups.map((block) => (
                            <div key={block.fieldName} className="space-y-2">
                              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-0.5">
                                {block.fieldLabel}
                              </div>
                              <div className="rounded-lg border border-dashed border-muted-foreground/25 bg-muted/40 px-3.5 py-3 text-xs text-muted-foreground">
                                No duplicate values
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </>
                  )
                ) : null}
              </div>
              {combineMode === "and" && andResults && andResults.groups.length > MAX_GROUPS_SHOWN ? (
                <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-200 dark:border-gray-700">
                  Showing first {MAX_GROUPS_SHOWN} groups.
                </div>
              ) : null}
            </div>
          )}

          {selectedFields.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">Select at least one field to see results.</p>
          ) : null}
        </div>

        <DialogFooter className="shrink-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
